// Native scheduling data access — schedules, visits, catalog, conflicts.
// Everything here reads CAERA's own tables (service_catalog, schedules,
// visits, visit_events); nothing touches AxisCare or the service_visits mirror.
import { supabase, lastFirst } from './supabase';
import { IN_SERVICE_STATUSES } from './serviceModel';

export const VISIT_SELECT = `
  id, visit_number, evv_visit_id, client_id, staff_id, service_id, schedule_id, schedule_date,
  scheduled_start, scheduled_end, actual_start, actual_end, status, evv_status, timezone,
  charge_rate, pay_rate, modification_reason, missed_reason_code, missed_action_code, care_note,
  client:clients ( id, client_id, first_name, last_name ),
  staff:staff ( id, first_name, last_name, full_name, position_code ),
  service:service_catalog ( id, code, name, skill_kind, color, caregiver_types, procedure_code, modifier1, evv_required )
`;

export const getServiceCatalog = () =>
  supabase.from('service_catalog').select('*').eq('active', true).order('sort_order');

export const getActiveClients = () =>
  supabase.from('clients')
    .select('id, client_id, first_name, last_name, current_status, pa_skilled_hours_week, pa_pss_hours_week, household_pets, household_pets_other, household_pets_notes')
    .in('current_status', IN_SERVICE_STATUSES)
    .order('last_name');

export const getFieldStaff = () =>
  supabase.from('staff')
    .select('id, first_name, last_name, full_name, position_code, status, employment_type, contracting_agency_id, bg_check_status, bg_check_type, bg_check_initiated_at, pet_restrictions')
    .eq('status', 'active')
    .order('last_name');

export const getSchedules = () =>
  supabase.from('schedules')
    .select(`*, client:clients ( id, client_id, first_name, last_name ),
             staff:staff ( id, first_name, last_name, full_name, position_code ),
             service:service_catalog ( id, code, name, skill_kind, color )`)
    .in('status', ['active', 'paused'])
    .order('created_at', { ascending: false });

export const createSchedule = (data) => supabase.from('schedules').insert([data]).select().single();
export const updateSchedule = (id, data) => supabase.from('schedules').update(data).eq('id', id).select().single();

export const getVisitsInRange = (startIso, endIso) =>
  supabase.from('visits')
    .select(VISIT_SELECT)
    .gte('scheduled_start', startIso)
    .lt('scheduled_start', endIso)
    .order('scheduled_start');

export const updateVisit = (id, data) => supabase.from('visits').update(data).eq('id', id).select(VISIT_SELECT).single();

export const getVisitEvents = (visitId) =>
  supabase.from('visit_events')
    .select('id, event_type, occurred_at, method, reason_code, note, changes, actor:profiles ( full_name )')
    .eq('visit_id', visitId)
    .order('occurred_at', { ascending: false });

// Overlapping live visits for a caregiver — used to warn before double-booking.
export const getConflicts = async (staffId, startIso, endIso, excludeVisitId) => {
  if (!staffId) return { data: [], error: null };
  let q = supabase.from('visits')
    .select('id, scheduled_start, scheduled_end, client:clients ( first_name, last_name, client_id )')
    .eq('staff_id', staffId)
    .in('status', ['scheduled', 'in_progress'])
    .lt('scheduled_start', endIso)
    .gt('scheduled_end', startIso);
  if (excludeVisitId) q = q.neq('id', excludeVisitId);
  return q;
};

export const regenerateAllVisits = (horizonDays = 28) =>
  supabase.rpc('generate_visits_from_schedules', { p_horizon_days: horizonDays });

// ── Display helpers ─────────────────────────────────────────────────────────
export const clientLabel = (c) => c ? `${lastFirst(c.first_name, c.last_name)}${c.client_id ? ` · ${c.client_id}` : ''}` : '—';
export const staffLabel  = (s) => s ? (s.first_name || s.last_name ? lastFirst(s.first_name, s.last_name) : s.full_name) : 'Open';
export const visitHours  = (v) => {
  const a = new Date(v.actual_start || v.scheduled_start), b = new Date(v.actual_end || v.scheduled_end);
  const h = (b - a) / 3600000;
  return Number.isFinite(h) && h > 0 ? Math.round(h * 100) / 100 : 0;
};

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const FREQUENCIES = [
  { value: 1, label: 'Every week' },
  { value: 2, label: 'Every 2 weeks' },
  { value: 3, label: 'Every 3 weeks' },
  { value: 4, label: 'Every 4 weeks' },
];

// Georgia / Netsmart missed-visit typecodes. Labels are working descriptions;
// verify against the Netsmart typecode dictionary before any line transmits.
export const MISSED_REASONS = [
  { code: 'PCAN', label: 'Participant cancelled' },
  { code: 'PNOS', label: 'Participant not home / no-show' },
  { code: 'HOSP', label: 'Participant hospitalised' },
  { code: 'REFF', label: 'Participant refused service' },
  { code: 'RCAN', label: 'Caregiver cancelled' },
  { code: 'RNOS', label: 'Caregiver no-show' },
  { code: 'RERR', label: 'Caregiver / agency error' },
  { code: 'PERR', label: 'Participant error' },
  { code: 'PAIS', label: 'Participant admitted to institution' },
  { code: 'OTHR', label: 'Other (explain in note)' },
];
export const MISSED_ACTIONS = [
  { code: 'CONT', label: 'Contacted participant / family' },
  { code: 'SCHS', label: 'Rescheduled same day' },
  { code: 'SCHD', label: 'Rescheduled to another day' },
  { code: 'RENW', label: 'Replacement caregiver sent' },
  { code: 'RESU', label: 'Service resumed next visit' },
  { code: 'OTHR', label: 'Other (explain in note)' },
];

// The EVV status a visit's status implies. The one mapping for every screen
// that edits a visit (Scheduling, Verification Desk), the same one
// caregiver_clock() applies: a finished visit is HOLD until verified.
const EVV_FOR_STATUS = { missed: 'UNBL', cancelled: 'UNSR', completed: 'HOLD', in_progress: 'INPR' };
export const evvStatusFor = (status) => EVV_FOR_STATUS[status] || 'NEWS';

export const STATUS_LABEL = {
  scheduled: 'Scheduled', in_progress: 'In progress', completed: 'Completed',
  missed: 'Missed', cancelled: 'Cancelled', incomplete: 'Incomplete',
};
export const SERVICE_COLOR = { skilled: 'var(--accent)', pss: 'var(--copper)', therapy: 'var(--info)', other: 'var(--zone-closed)' };

// ── Verification desk ───────────────────────────────────────────────────────
export const getVisitReview = (startIso, endIso) =>
  supabase.from('visit_review').select('*')
    .gte('scheduled_start', startIso).lt('scheduled_start', endIso)
    .order('scheduled_start');

export const verifyVisit = (id, { billableStart, billableEnd, note } = {}) =>
  supabase.rpc('verify_visit', { p_visit_id: id, p_billable_start: billableStart ?? null, p_billable_end: billableEnd ?? null, p_note: note ?? null });

export const unverifyVisit = (id, reason) =>
  supabase.rpc('unverify_visit', { p_visit_id: id, p_reason: reason });

export const getEvvSettings = () => supabase.from('evv_settings').select('*').limit(1).maybeSingle();
export const saveEvvSettings = (agencyId, data) =>
  supabase.from('evv_settings').update({ ...data, updated_at: new Date().toISOString() }).eq('agency_id', agencyId).select().single();

export const getReasonCodes = () => supabase.from('evv_reason_codes').select('*').eq('active', true).order('sort_order');

export const EXCEPTION_LABELS = {
  NO_CLOCK_IN:       { label: 'No clock-in',            severity: 'block' },
  NO_CLOCK_OUT:      { label: 'No clock-out',           severity: 'block' },
  LATE_CLOCK_IN:     { label: 'Late clock-in',          severity: 'live' },
  LATE_CLOCK_OUT:    { label: 'Late clock-out',         severity: 'live' },
  NO_GPS_IN:         { label: 'No GPS at clock-in',     severity: 'warn' },
  NO_GPS_OUT:        { label: 'No GPS at clock-out',    severity: 'warn' },
  OUT_OF_GEOFENCE:   { label: 'Outside geofence',       severity: 'warn' },
  START_VARIANCE:    { label: 'Start-time variance',    severity: 'warn' },
  DURATION_VARIANCE: { label: 'Duration variance',      severity: 'warn' },
  OPEN_TASKS:        { label: 'Required tasks open',    severity: 'warn' },
  MANUAL_EDIT:       { label: 'Edited by office',       severity: 'info' },
  NO_DIAGNOSIS:      { label: 'No diagnosis code',      severity: 'block' },
  NO_REFERRING_NPI:  { label: 'No referring NPI',       severity: 'block' },
  UNSIGNED:          { label: 'No client signature',    severity: 'warn' },
  MISSED_NO_REASON:  { label: 'Missed without reason',  severity: 'block' },
  EVV_REJECTED:      { label: 'Rejected by aggregator', severity: 'block' },
  EVV_ERROR:         { label: 'Transmission error',     severity: 'warn'  },
};
export const SEVERITY_STYLE = {
  block: { color: 'var(--danger)', bg: 'var(--danger-bg)', border: 'var(--danger-border)' },
  live:  { color: 'var(--danger)', bg: 'var(--danger-bg)', border: 'var(--danger-border)' },
  warn:  { color: 'var(--warn)',   bg: 'var(--warn-bg)',   border: 'var(--warn-border)' },
  info:  { color: 'var(--info)',   bg: 'var(--info-bg)',   border: 'var(--info-border)' },
};

// ── EVV feed (aggregator transmitter) ───────────────────────────────────────
export const getEvvFeedStatus = () => supabase.functions.invoke('evv-transmit', { body: { action: 'status' } });
export const runEvvFeed = () => supabase.functions.invoke('evv-transmit', { body: { action: 'run', trigger: 'manual' } });
export const getEvvBatches = (limit = 30) =>
  supabase.from('evv_batches').select('*, created_by_profile:profiles ( full_name )').order('created_at', { ascending: false }).limit(limit);
export const getEvvTransmissions = (batchId) =>
  supabase.from('evv_transmissions')
    .select('id, visit_id, evv_status_sent, split_part, response_status, response_code, response_message, record_line, payload, sent_at, visit:visits ( evv_visit_id, client:clients ( first_name, last_name, client_id ) )')
    .eq('batch_id', batchId).order('id');

export const geocodeClient = (clientId) => supabase.functions.invoke('geocode-clients', { body: clientId ? { client_id: clientId } : {} });

export const getVisitSignatures = (visitId) =>
  supabase.from('visit_signatures').select('id, kind, signer_name, relationship, svg_path, aspect_ratio, signed_at').eq('visit_id', visitId).order('signed_at');

// ── Shift requests, time off, availability (office side) ───────────────────
export const getShiftClaims = (startIso, endIso) =>
  supabase.from('shift_claims')
    .select('id, visit_id, staff_id, status, note, created_at, decision_note, staff:staff ( id, first_name, last_name, full_name, position_code ), visit:visits!inner ( id, scheduled_start, scheduled_end, staff_id, client:clients ( first_name, last_name, client_id ), service:service_catalog ( name ) )')
    .eq('status', 'requested')
    .gte('visit.scheduled_start', startIso).lt('visit.scheduled_start', endIso)
    .order('created_at');
export const acceptShiftClaim = (claimId, note) => supabase.rpc('accept_shift_claim', { p_claim_id: claimId, p_note: note ?? null });
export const declineShiftClaim = (claimId, note) => supabase.rpc('decline_shift_claim', { p_claim_id: claimId, p_note: note ?? null });

export const getTimeOffRequests = (status = 'requested') =>
  supabase.from('time_off_requests')
    .select('id, staff_id, start_date, end_date, reason, status, created_at, decision_note, staff:staff ( id, first_name, last_name, full_name, position_code )')
    .eq('status', status).order('start_date');
export const decideTimeOff = (id, approve, note) => supabase.rpc('decide_time_off', { p_request_id: id, p_approve: approve, p_note: note ?? null });

export const getApprovedTimeOff = (staffId, startDate, endDate) =>
  supabase.from('time_off_requests').select('id, start_date, end_date, reason')
    .eq('staff_id', staffId).eq('status', 'approved').lte('start_date', endDate).gte('end_date', startDate);

export const getStaffAvailability = (staffId) =>
  supabase.from('staff_availability').select('day_of_week, start_time, end_time').eq('staff_id', staffId).order('day_of_week');
